import React, { useEffect, useState } from "react";
import { Card, Row, Col, Statistic, Table, Button, Space } from "antd";
import { UserOutlined, ShopOutlined, ShoppingOutlined, DollarOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalCompanies: 0,
    activeCompanies: 0,
    activeUsers: 0,
  });
  const [recentCompanies, setRecentCompanies] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [usersRes, companiesRes] = await Promise.all([
        axios.get("super-admin/users"),
        axios.get("super-admin/companies"),
      ]);

      const users = usersRes.data;
      const companies = companiesRes.data;

      setStats({
        totalUsers: users.length,
        totalCompanies: companies.length,
        activeUsers: users.filter((u) => u.status).length,
        activeCompanies: companies.filter((c) => c.status).length,
      });

      // Get recent companies
      const recent = companies
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
        .slice(0, 5);
      setRecentCompanies(recent);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const companyColumns = [
    {
      title: "Company Name",
      dataIndex: "company_name",
      key: "company_name",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (status ? "Active" : "Inactive"),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button type="link" onClick={() => navigate(`/super-admin/companies/${record.id}/view`)}>
          View Details
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <h1>Super Admin Dashboard</h1>

      <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Users"
              value={stats.totalUsers}
              prefix={<UserOutlined />}
              loading={loading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Active Users"
              value={stats.activeUsers}
              prefix={<UserOutlined />}
              loading={loading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Companies"
              value={stats.totalCompanies}
              prefix={<ShopOutlined />}
              loading={loading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Active Companies"
              value={stats.activeCompanies}
              prefix={<ShopOutlined />}
              loading={loading}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card
            title="Quick Actions"
            extra={
              <Space>
                <Button type="primary" onClick={() => navigate("/super-admin/users")}>
                  Manage Users
                </Button>
                <Button onClick={() => navigate("/super-admin/companies")}>
                  Manage Companies
                </Button>
              </Space>
            }
          >
            <Space direction="vertical" style={{ width: "100%" }}>
              <Button
                block
                onClick={() => navigate("/super-admin/users")}
                style={{ textAlign: "left" }}
              >
                View All Users
              </Button>
              <Button
                block
                onClick={() => navigate("/super-admin/companies")}
                style={{ textAlign: "left" }}
              >
                View All Companies
              </Button>
              <Button
                block
                onClick={() => navigate("/super-admin/products")}
                style={{ textAlign: "left" }}
              >
                Manage Master Data (Products)
              </Button>
              <Button
                block
                onClick={() => navigate("/super-admin/book-publishers")}
                style={{ textAlign: "left" }}
              >
                Manage Book Publishers
              </Button>
              <Button
                block
                onClick={() => navigate("/super-admin/product-categories")}
                style={{ textAlign: "left" }}
              >
                Manage Product Categories
              </Button>
              <Button
                block
                onClick={() => navigate("/super-admin/product-currencies")}
                style={{ textAlign: "left" }}
              >
                Manage Product Currencies
              </Button>
            </Space>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="Recent Companies">
            <Table
              dataSource={recentCompanies}
              columns={companyColumns}
              pagination={false}
              loading={loading}
              rowKey="id"
              size="small"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SuperAdminDashboard;
